import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format } from "date-fns";
import { CheckCheck, FileText, MessageSquare, PencilLine } from "lucide-react";

interface TimelineEvent {
  id: string;
  created_at: string;
  type: string;
  status?: string;
  comment?: string;
  responsible: string;
  details?: string;
}

interface HistoryTimelineProps {
  history: TimelineEvent[] | undefined;
}

const TYPE_TRANSLATIONS = {
  CREATION: "Criação",
  UPDATED: "Atualização",
  COMMENT: "Comentário",
  FINALIZED: "Finalizado",
} as const;

const STATUS_TRANSLATIONS = {
  PENDING: "Pendente",
  APPROVED: "Aprovado",
  REJECTED: "Rejeitado",
  TERM_SIGNED: "Assinado",
  TERM_PENDING_SIGNATURE: "Assinatura Pendente",
  CANCELED: "Cancelado",
} as const;

const getStatusBadgeVariant = (status: string) => {
  switch (status) {
    case "APPROVED":
      return "bg-green-500/90 hover:bg-green-500 text-white";
    case "REJECTED":
      return "bg-red-500/90 hover:bg-red-500 text-white";
    case "PENDING":
      return "bg-amber-500/90 hover:bg-amber-500 text-white";
    case "TERM_SIGNED":
      return "bg-blue-500/90 hover:bg-blue-500 text-white";
    case "TERM_PENDING_SIGNATURE":
      return "bg-orange-500/90 hover:bg-orange-500 text-white";
    default:
      return "bg-gray-500/90 hover:bg-gray-500 text-white";
  }
};

export const formatComment = (comment: string) => {
  return comment.split("\n").map((line, index) => (
    <span key={index}>
      {line.includes("ASSINATURA REJEITADA") ? (
        <>
          <strong>ASSINATURA REJEITADA:</strong>
          {line.replace("ASSINATURA REJEITADA:", "")}
        </>
      ) : line.includes("RECUSADO") ? (
        <>
          <strong>RECUSADO:</strong>
          {line.replace("RECUSADO:", "")}
        </>
      ) : line.includes("CANCELADO") ? (
        <>
          <strong>CANCELADO:</strong>
          {line.replace("CANCELADO:", "")}
        </>
      ) : (
        line
      )}
      <br />
    </span>
  ));
};

export function HistoryTimeline({ history }: HistoryTimelineProps) {
  const getIconForEventType = (type: string) => {
    const icons = {
      CREATION: <FileText className="w-6 h-6 text-blue-500" />,
      UPDATED: <PencilLine className="w-6 h-6 text-blue-500" />,
      COMMENT: <MessageSquare className="w-6 h-6 text-green-500" />,
      FINALIZED: <CheckCheck className="w-6 h-6 text-green-500" />,
    };
    return (
      icons[type as keyof typeof icons] || (
        <FileText className="w-6 h-6 text-gray-500" />
      )
    );
  };

  const translateType = (type: string) => {
    return TYPE_TRANSLATIONS[type as keyof typeof TYPE_TRANSLATIONS] || type;
  };

  const translateStatus = (status: string) => {
    return (
      STATUS_TRANSLATIONS[status as keyof typeof STATUS_TRANSLATIONS] || status
    );
  };

  return (
    <div className="h-full">
      <ScrollArea className="h-full pr-4">
        <div className="relative p-4">
          {history?.map((event, index) => (
            <div key={event.id} className="mb-8 flex mt-4 group">
              <div className="flex flex-col items-center mr-4">
                <div className="flex justify-center items-center w-10 h-10 rounded-full bg-gray-100 border border-gray-200 shadow-sm group-hover:shadow group-hover:bg-gray-50 transition-all">
                  {getIconForEventType(event.type)}
                </div>
                {index !== history.length - 1 && (
                  <div className="w-0.5 h-full bg-gray-200 mt-2" />
                )}
              </div>
              <div className="flex-grow">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h3 className="text-base font-semibold text-gray-800">
                    {translateType(event.type)}
                  </h3>
                  {event.status && (
                    <Badge
                      className={`${getStatusBadgeVariant(
                        event.status
                      )} text-xs px-2 py-0.5 rounded-full`}
                    >
                      {translateStatus(event.status)}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-gray-500 mb-2">
                  {format(new Date(event.created_at), "dd/MM/yyyy HH:mm")}
                </p>
                {event.comment && (
                  <p className="text-sm mb-2 bg-gray-50 p-2 rounded-md border border-gray-100">
                    {formatComment(event.comment)}
                  </p>
                )}
                <p className="text-xs text-gray-500 italic">
                  Responsável:{" "}
                  <span className="font-medium">{event.responsible}</span>
                </p>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
